import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  LinearProgress,
  Link,
  List,
  ListItem,
  ListItemIcon,
  Typography,
} from "@mui/material";
import { useDropzone } from "react-dropzone";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import { UPLOAD_FAILED_EMERGENCYORDER } from "services/CONSTANTS";
import { showErrorMessageByCode } from "services/commonServices";
import { showNotificationDialog } from "redux/actions/notificationActions";
import { useDispatch, useSelector } from "react-redux";
import CSVFileValidator from "csv-file-validator";
import {
  hideNotificationDialogManualPOUpload,
  showNotificationDialogManualPOUpload,
} from "redux/actions/notificationManualPOUploadAction";
import {
  resetUploadPurchaseOrder,
  uploadManualPoFile,
} from "redux/actions/uploadManualPoAction";
import { encryptString } from "components/common/EncryptDecrypt";
import AlertDialogManualPOUpload from "components/common/AlertDialogManualPOUpload";
import { SelectReasonDialog } from "components/common/SelectReasonDialog";
import { showNotificationDialogManualOrder } from "redux/actions/notificationManualOrderAction";
import { useNavigate } from "react-router-dom";
import { PAGE, PO_ELEMENTS, PO_REASON } from "components/common/CONSTANTS";

const baseStyle = {
  flex: 1,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  padding: "20px",
  borderWidth: 2,
  borderRadius: 2,
  borderColor: "#eeeeee",
  borderStyle: "dashed",
  backgroundColor: "#fafafa",
  color: "#bdbdbd",
  outline: "none",
  transition: "border .24s ease-in-out",
};

const focusedStyle = {
  borderColor: "#2196f3",
};

const acceptStyle = {
  borderColor: "#00e676",
};

const rejectStyle = {
  borderColor: "#ff1744",
};

export const UploadManualPO = () => {
  const [acceptedFiles, setAcceptedFiles] = useState([]);
  const [fileError, setFileError] = useState(null);
  const [showFileUploadPopup, setShowFileUploadPopup] = useState(false);
  const [isErrorPopupFlag, setIsErrorPopupFlag] = useState(true);
  const [dialogTitle, setDialogTitle] = useState("");
  const [showDialog, setShowDialog] = useState(false);
  const [dialogmessage, setDialogMessage] = useState("");
  const [selectedReasonCode, setSelectedReasonCode] = useState(null);

  const dispatch = useDispatch();
  const userType = useSelector((state) => state.user);
  const { accessiblePages } = userType.userType;
  const loginResponse = userType.loginResponse;
  const [accessibleButtonlist, setAccessibleButtonlist] = React.useState([]);

  const isuploadManualPoError = useSelector(
    (state) => state.purchaseOrderUpload.isuploadPurchaseOrderError
  );
  const isuploadManualPoSuccess = useSelector(
    (state) => state.purchaseOrderUpload.isuploadPurchaseOrderSuccess
  );

  const isuploadManualPoResponse = useSelector(
    (state) => state.purchaseOrderUpload.uploadPurchaseOrderResponse
  );
  const errorDetails = useSelector(
    (state) => state.purchaseOrderUpload?.errorDetails?.data
  );

  useEffect(() => {
    if (accessiblePages) {
      setAccessibleButtonlist(
        accessiblePages[PAGE.UPLOAD_CREATE_MANUAL_ORDER_PO.key]
      );
    }
  }, [accessiblePages]);

  useEffect(() => {
    setShowDialog(false);
    if (isuploadManualPoSuccess) {
      setShowFileUploadPopup(false);
    }

    if (
      isuploadManualPoError &&
      isErrorPopupFlag &&
      !errorDetails?.errorMessage?.includes(
        "Validation errors in CSV file: Invalid date format"
      )
    ) {
      setShowFileUploadPopup(false);
      setShowDialog(false);
      const { errTitle, errContent } = showErrorMessageByCode(
        isuploadManualPoResponse
      );
      dispatch(showNotificationDialog(errTitle, errContent, "Done"));
      setShowFileUploadPopup(false);
      setShowDialog(false);
      dispatch(hideNotificationDialogManualPOUpload());
      setIsErrorPopupFlag(false);
      dispatch(resetUploadPurchaseOrder());
    }
    if (
      isuploadManualPoError &&
      isErrorPopupFlag &&
      errorDetails?.errorMessage?.includes(
        "Validation errors in CSV file: Invalid date format"
      )
    ) {
      setShowFileUploadPopup(false);
      dispatch(
        showNotificationDialogManualOrder(
          "Upload Failed",
          "Incorrect file format, please check column headers, column sequence, column data and upload file with correct template.",
          "Done"
        )
      );
      setShowFileUploadPopup(false);
      setIsErrorPopupFlag(false);
      setShowDialog(false);
      dispatch(hideNotificationDialogManualPOUpload());
      dispatch(resetUploadPurchaseOrder());
    }

    if (
      isuploadManualPoSuccess &&
      isuploadManualPoResponse.status === "success"
    ) {
      setDialogTitle("Select Manual Purchase Order Reason");
      setDialogMessage("Please select manual purchase order creation reason");
      setShowDialog(true);
    }
  }, [
    isuploadManualPoSuccess,
    dispatch,
    isErrorPopupFlag,
    isuploadManualPoError,
    isuploadManualPoResponse,
    errorDetails,
  ]);

  const onDrop = useCallback(
    (files, fileRejections) => {
      if (!files.length) {
        const showErrorMessage = fileRejections
          .flatMap((x) => x.errors)
          .map((x) => x.message)
          .join(",");
        if (showErrorMessage === "File type must be text/csv,.csv") {
          let str = showErrorMessage.replace(
            showErrorMessage,
            "Incorrect file format, please upload CSV file."
          );
          setFileError(str);
          dispatch(
            showNotificationDialogManualOrder("Upload Failed", str, "Done")
          );
        } else {
          setFileError(null);
        }
      } else {
        setFileError(null);
      }

      setAcceptedFiles([...acceptedFiles, ...files]);
      setShowFileUploadPopup(false);
    },
    [acceptedFiles, dispatch]
  );

  const {
    fileRejections,
    getRootProps,
    getInputProps,
    isFocused,
    isDragAccept,
    isDragReject,
  } = useDropzone({
    onDrop,
    accept: {
      "text/csv": [".csv"],
    },
    multiple: false,
    disabled: acceptedFiles.length,
  });

  const style = useMemo(
    () => ({
      ...baseStyle,
      ...(isFocused ? focusedStyle : {}),
      ...(isDragAccept ? acceptStyle : {}),
      ...(isDragReject ? rejectStyle : {}),
    }),
    [isFocused, isDragAccept, isDragReject]
  );

  const removeFile = (file) => () => {
    const files = [...acceptedFiles];
    files.splice(files.indexOf(file), 1);
    setAcceptedFiles(files);
  };

  const showAcceptedFiles = acceptedFiles.map((file) => (
    <ListItem key={file.path} sx={{ pl: 0 }} dense={true} disablePadding={true}>
      <ListItemIcon sx={{ minWidth: "auto", pr: 1 }}>
        <DescriptionOutlinedIcon />
      </ListItemIcon>
      {file.path}
      <Button component={Link} onClick={removeFile(file)} color="error">
        Remove
      </Button>
    </ListItem>
  ));

  const fileRejectionItems = fileRejections.map(({ file }) => (
    <>
      <ListItem
        key={file.path}
        sx={{ pl: 0 }}
        dense={true}
        disablePadding={true}
      >
        <ListItemIcon sx={{ minWidth: "auto", pr: 1, color: "red" }}>
          <DescriptionOutlinedIcon />
        </ListItemIcon>
        {file.path}
      </ListItem>
    </>
  ));

  const validate = () => {
    const errors = {};
    if (!acceptedFiles.length) {
      errors.acceptedFilesText = "Please select a file to proceed";
      setFileError("Please select a valid file to proceed");
    }
    return !Object.keys(errors).length;
  };

  function isNumeric(str) {
    return /^\d+$/.test(str.trim());
  }

  function validateImportValue(value) {
    return value === "" || value === null || /^(yes|no)$/i.test(value.trim());
  }
  function validateHandballValue(value) {
    return value === "" || value === null || /^(yes|no)$/i.test(value.trim());
  }

  function validateOrderQtyInCases(value) {
    if (value === "") return true;
    return /^\d{1,8}$/.test(value.trim());
  }

  function validateOrderQtyInPallets(value) {
    if (value === "") return true;
    return /^(\d{1,5}(\.\d{1,2})?)$/.test(value.trim());
  }

  const handleUploadFile = (e) => {
    e.preventDefault();
    if (!validate()) return;

    const config = {
      headers: [
        {
          name: "SupplierSiteId",
          inputName: "SupplierSiteId",
          required: true,
          validate: function (value) {
            if (value === "") {
              return true;
            }
            return isNumeric(value);
          },
        },
        {
          name: "DestinationLocation",
          inputName: "DestinationLocation",
          required: true,
          validate: function (value) {
            if (value === "") {
              return true;
            }
            return isNumeric(value);
          },
        },
        {
          name: "OrderDate",
          inputName: "OrderDate",
          required: true,
          validate: function (value) {
            if (value === "") {
              return true;
            }
            return isNumeric(value);
          },
        },
        {
          name: "DeliveryDate",
          inputName: "DeliveryDate",
          required: true,
          validate: function (value) {
            if (value === "") {
              return true;
            }
            return isNumeric(value);
          },
        },
        {
          name: "PIN",
          inputName: "PIN",
          required: true,
          validate: function (value) {
            return isNumeric(value);
          },
        },
        {
          name: "OrderQtyInCases",
          inputName: "OrderQtyInCases",
          required: false,
          validate: function (value) {
            return validateOrderQtyInCases(value);
          },
        },
        {
          name: "OrderQtyInPallets",
          inputName: "OrderQtyInPallets",
          required: false,
          validate: function (value) {
            return validateOrderQtyInPallets(value);
          },
        },
        {
          name: "Handball",
          inputName: "Handball",
          validate: function (value) {
            return validateHandballValue(value);
          },
        },
        {
          name: "Import",
          inputName: "Import",
          validate: function (value) {
            return validateImportValue(value);
          },
        },
      ],
    };


    CSVFileValidator(acceptedFiles[0], config)
      .then(async (csvData) => {
        const { inValidData, data } = csvData;
        if (csvData.data.length === 0) {
          dispatch(
            showNotificationDialogManualOrder(
              "Upload Failed",
              "Incorrect file format, please check column headers, column sequence, column data and upload file with correct template.",
              "Done"
            )
          );
        }
        data.forEach((row, index) => {
          const casesValue = row.OrderQtyInCases?.trim();
          const palletsValue = row.OrderQtyInPallets?.trim();
          const isEmptyCases = !casesValue || casesValue === "0";
          const isEmptyPallets =
            !palletsValue || /^(0|0\.0|0\.00)$/.test(palletsValue);
          if (
            (isEmptyCases && isEmptyPallets) ||
            (casesValue === "0" && !palletsValue) ||
            (!casesValue && isEmptyPallets)
          ) {
            inValidData.push({
              rowIndex: index + 1,
              columnIndex: 6,
              message:
                "OrderQtyInCases or OrderQtyInPallets must have at least one valid value.",
            });
          }
        });

        const GENERIC_MSG =
          "Incorrect file format, please check column headers, column sequence, column data, and upload the file using the correct template.";

        if (inValidData.length) {
          const quantityErrors = [];

          let hasNonQuantityError = false;

          inValidData.forEach((x) => {
            const msg = x.message;

            if (msg.includes("OrderQtyInCases is not valid")) {
              quantityErrors.push(
                "Order Quantity in Cases should be an integer with up to 8 digits."
              );
            } else if (msg.includes("OrderQtyInPallets is not valid")) {
              quantityErrors.push(
                "Order Quantity in Pallets should be a number with up to 5 digits before the decimal and up to 2 digits after the decimal."
              );
            } else if (
              msg.includes(
                "OrderQtyInCases or OrderQtyInPallets must have at least one valid value."
              )
            ) {
              quantityErrors.push(
                "Either Order Quantity in Cases or Order Quantity in Pallets must have a valid value — both cannot be empty or zero."
              );
            } else {
              hasNonQuantityError = true; 
            }
          });
          let notificationContent;
          if (hasNonQuantityError) {
            notificationContent = GENERIC_MSG;
          } else {
            const uniqueQuantityErrors = [...new Set(quantityErrors)];
            const errorListItems = uniqueQuantityErrors.map((msg, index) => (
              <ListItem dense sx={{ p: 0 }} key={index}>
                {`${index + 1}. ${msg}`}
              </ListItem>
            ));
            notificationContent = (
              <>
                <Typography sx={{ mb: 1 }}>{GENERIC_MSG}</Typography>
                <List dense>{errorListItems}</List>
              </>
            );
          }

          dispatch(
            showNotificationDialogManualOrder(
              "Upload Failed",
              notificationContent,
              "Done"
            )
          );
          return;
        }

        if (csvData.data.length !== 0) {
          setShowDialog(false);
          setShowFileUploadPopup(true);
          const PoRecordsFileForReupload = localStorage.getItem(
            "PoRecordsFileForReupload"
          );
          const jobIdForReupload = localStorage.getItem("jobIdForReupload");
          const encryptedEmailId = await encryptString(loginResponse?.email);
          if (acceptedFiles[0].name === PoRecordsFileForReupload) {
            const formData = new FormData();
            formData.append("file", acceptedFiles[0]);
            formData.append("userId", encryptedEmailId);
            formData.append("status", "error");
            formData.append("jobId", jobIdForReupload);
            formData.append("fileName", PoRecordsFileForReupload);
            setIsErrorPopupFlag(true);
            let payload = {
              params: formData,
            };

            dispatch(uploadManualPoFile(payload));
          } else {
            const formData = new FormData();
            formData.append("file", acceptedFiles[0]);
            formData.append("userId", encryptedEmailId);
            setIsErrorPopupFlag(true);
            let payload = {
              params: formData,
            };

            dispatch(uploadManualPoFile(payload));
          }
        }
      })
      .catch((err) => {
        setShowFileUploadPopup(false);
        console.error("error", err);
        dispatch(
          showNotificationDialogManualOrder(
            "Upload Failed",
            UPLOAD_FAILED_EMERGENCYORDER,
            "Done"
          )
        );
      });
  };
  useEffect(() => {
    const shouldTriggerUpload = localStorage.getItem("triggerUpload");
    if (shouldTriggerUpload === "true") {
      localStorage.removeItem("triggerUpload");
      document.querySelector("input[data-testid='dropzone']").click();
    }
  }, []);


  const onConfirmReasonClicked = (reason) => {
    setSelectedReasonCode(reason);
    setShowDialog(false);
    let popupmsg =
      "PO's have been successfully created and are ready for preview.";
    if (
      accessibleButtonlist.includes(
        PO_ELEMENTS.UPLOAD_PO.CONFIRM_WITHOUT_REVIEW
      )
    ) {
      popupmsg =
        popupmsg +
        "If you wish to confirm without reviewing please select 'Confirm' else select 'Review'.";
    }

    dispatch(
      showNotificationDialogManualPOUpload(
        "PO Upload",
        popupmsg,
        "CONFIRM WITHOUT REVIEW",
        "REVIEW",
        "/posummary",
        accessibleButtonlist.includes(
          PO_ELEMENTS.UPLOAD_PO.CONFIRM_WITHOUT_REVIEW
        ),
        accessibleButtonlist.includes(PO_ELEMENTS.UPLOAD_PO.REVIEW_BUTTON)
      )
    );
  };
  const navigate = useNavigate();

  if (
    isuploadManualPoSuccess &&
    isuploadManualPoResponse.status === "partial-success"
  ) {
    navigate(PAGE.UPLOAD_MANUAL_PO_ERROR.url);
  }
  const handleUploadConfirm = async () => {
    setShowDialog(false);
    try {
      const encryptedEmailId = await encryptString(loginResponse?.email);

      const formData = new FormData();
      formData.append("userId", encryptedEmailId);
      formData.append(
        "requestData",
        JSON.stringify({
          fileName: isuploadManualPoResponse?.fileName,
          jobId: isuploadManualPoResponse?.jobId,
          status: "confirmed",
        })
      );
      formData.append("reasonCode", selectedReasonCode);

      let payload = {
        params: formData,
      };

      dispatch(uploadManualPoFile(payload));
    } catch (error) {
      setShowFileUploadPopup(false);
      console.error("Upload failed:", error);

      dispatch(("Upload Failed", UPLOAD_FAILED_EMERGENCYORDER, "Done"));
    }
  };

  const handleUploadReview = async () => {
    setShowDialog(false);
    try {
      const encryptedEmailId = await encryptString(loginResponse?.email);

      const formData = new FormData();
      formData.append("userId", encryptedEmailId);
      formData.append(
        "requestData",
        JSON.stringify({
          fileName: isuploadManualPoResponse?.fileName,
          jobId: isuploadManualPoResponse?.jobId,
          status: "review",
        })
      );
      formData.append("reasonCode", selectedReasonCode);
      let payload = {
        params: formData,
      };

      dispatch(uploadManualPoFile(payload));
    } catch (error) {
      setShowFileUploadPopup(false);
      console.error("Upload failed:", error);

      dispatch(("Upload Failed", UPLOAD_FAILED_EMERGENCYORDER, "Done"));
    }
  };
  return (
    <>
      <AlertDialogManualPOUpload
        onConfirmUpload={handleUploadConfirm}
        isuploadManualPoResponse={isuploadManualPoResponse}
        isuploadManualPoError={isuploadManualPoError}
        onReviewUpload={handleUploadReview}
      />
      <SelectReasonDialog
        reasonType={PO_REASON.ORDER_SUB_TYPE.CREATE}
        title={dialogTitle}
        message={dialogmessage}
        selectReasonDialog={showDialog}
        handleConfirmReason={onConfirmReasonClicked}
      />
      {showFileUploadPopup && !isuploadManualPoSuccess ? (
        <Dialog open={true} maxWidth="sm" fullWidth={true}>
          <DialogTitle>File Uploading</DialogTitle>
          <DialogContent>
            <LinearProgress />
            <Typography>
              To ensure the successful upload, please avoid clicking the back
              button, leaving the page, or refreshing it during the upload
              process
            </Typography>
          </DialogContent>
        </Dialog>
      ) : (
        <div></div>
      )}
      <Grid
        container
        rowSpacing={2}
        columnSpacing={1}
        component="form"
        onSubmit={handleUploadFile}
        sx={{ "& .MuiFormHelperText-root": { ml: 0 } }}
      >
        <Grid container spacing={1} item>
          <Grid item xs={12}>
            <div {...getRootProps({ style })} className="uploadPageStyling">
              <input data-testid="dropzone" {...getInputProps()} />
              <FileUploadOutlinedIcon fontSize="large" />
              <span>Drag and drop to upload or browse for files</span>
              <Typography variant="body2">(Only csv files allowed)</Typography>
              {isDragReject && (
                <Typography color="error.main">
                  Some file types are not supported
                </Typography>
              )}
            </div>
            <Typography variant="caption" color="error.main">
              {!showAcceptedFiles.length ? fileError : ""}
            </Typography>
            {showAcceptedFiles && (
              <>
                <aside className="asideStyle">
                  <List sx={{ pt: 0.5 }} dense={true}>
                    {showAcceptedFiles} {fileRejectionItems}
                  </List>
                </aside>
              </>
            )}
          </Grid>
        </Grid>
        <div className="commonButtons">
          <Grid container item sx={{ textAlign: "left" }}>
            <Grid item xs={4} sm={2}></Grid>
            <Grid item xs={4} sm={2}>
              {acceptedFiles[0]?.path ? (
                <Button variant="contained" type="submit">
                  Upload
                </Button>
              ) : (
                <Button variant="contained" type="submit" disabled>
                  Upload
                </Button>
              )}
              <Grid sx={{ paddingBottom: 17 }}></Grid>
            </Grid>
          </Grid>
        </div>
      </Grid>
      <Grid></Grid>
    </>
  );
};
